import { useCallback, useMemo, useRef } from "react";
import { type FieldValues, type Path, type UseFormReturn } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { getStepWorstSeverity } from "./stepErrors";
import type { SectionNavItem, StepConfig } from "./types";
import { getNestedError } from "./utils/formResolver";

type UseMultiStepFormOptions<TFormData extends FieldValues> = {
	formMethods: UseFormReturn<TFormData>;
	steps: StepConfig<TFormData>[];
};

export type StepValidationResult = { ok: true } | { ok: false; errorFieldPaths: string[] };

type UseMultiStepFormReturn<TFormData extends FieldValues> = {
	currentStep: StepConfig<TFormData>;
	currentStepIndex: number;
	sectionNavItems: SectionNavItem[];
	goToStep: (name: string) => Promise<StepValidationResult>;
	goToNextStep: () => Promise<StepValidationResult>;
	goToPreviousStep: () => Promise<void>;
	isLastStep: boolean;
	hasPreviousStep: boolean;
};

const NO_OP_RESULT: StepValidationResult = { ok: true };

const collectErrorLeafPaths = (node: unknown, prefix: string): string[] => {
	if (node == null || typeof node !== "object") return [];
	const record = node as Record<string, unknown>;
	if ("type" in record && "message" in record) {
		return record.type === "error" ? [prefix] : [];
	}
	const paths: string[] = [];
	for (const [key, child] of Object.entries(record)) {
		const childPath = prefix ? `${prefix}.${key}` : key;
		paths.push(...collectErrorLeafPaths(child, childPath));
	}
	return paths;
};

export const useMultiStepForm = <TFormData extends FieldValues>({
	formMethods,
	steps,
}: UseMultiStepFormOptions<TFormData>): UseMultiStepFormReturn<TFormData> => {
	const [searchParams, setSearchParams] = useSearchParams();
	const currentStepName = searchParams.get("section") ?? steps[0]?.name;

	// Track which steps have been validated (triggered when navigating away)
	const validatedStepsRef = useRef<Set<string>>(new Set());

	const stepKeyMap = useMemo(
		() => new Map(steps.map((step) => [step.name, step.fieldPaths as string[]])),
		[steps],
	);

	const currentStepIndex = useMemo(
		() =>
			Math.max(
				0,
				steps.findIndex((s) => s.name === currentStepName),
			),
		[steps, currentStepName],
	);

	const currentStep = steps[currentStepIndex] ?? steps[0];

	const navigateTo = useCallback(
		(name: string) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				next.set("section", name);
				return next;
			});
		},
		[setSearchParams],
	);

	const goToStep = useCallback(
		async (name: string): Promise<StepValidationResult> => {
			const currentKeys = stepKeyMap.get(currentStep.name) ?? [];
			let result: StepValidationResult = { ok: true };
			if (currentKeys.length > 0) {
				const passed = await formMethods.trigger(currentKeys as Path<TFormData>[]);
				validatedStepsRef.current.add(currentStep.name);
				if (!passed) {
					const errors = formMethods.formState.errors as Record<string, unknown>;
					const errorFieldPaths = [
						...new Set(
							currentKeys.flatMap((path) =>
								collectErrorLeafPaths(getNestedError(errors, path), path),
							),
						),
					];
					result = { ok: false, errorFieldPaths };
				}
			}
			navigateTo(name);
			return result;
		},
		[currentStep.name, stepKeyMap, formMethods, navigateTo],
	);

	const goToNextStep = useCallback(async (): Promise<StepValidationResult> => {
		const nextIndex = currentStepIndex + 1;
		if (nextIndex < steps.length) {
			return await goToStep(steps[nextIndex].name);
		}
		return NO_OP_RESULT;
	}, [currentStepIndex, steps, goToStep]);

	const goToPreviousStep = useCallback(async () => {
		const prevIndex = currentStepIndex - 1;
		if (prevIndex >= 0) {
			await goToStep(steps[prevIndex].name);
		}
	}, [currentStepIndex, steps, goToStep]);

	const { errors, isSubmitted } = formMethods.formState;

	const sectionNavItems: SectionNavItem[] = steps.map((step) => {
		const keys = stepKeyMap.get(step.name) ?? [];
		const shouldShowErrors = isSubmitted || validatedStepsRef.current.has(step.name);
		const severity = shouldShowErrors ? getStepWorstSeverity(errors, keys) : null;
		const navSeverity = severity === "error" ? severity : null;
		return {
			name: step.name,
			label: step.label,
			severity: navSeverity,
		};
	});

	return {
		currentStep,
		currentStepIndex,
		sectionNavItems,
		goToStep,
		goToNextStep,
		goToPreviousStep,
		isLastStep: currentStepIndex === steps.length - 1,
		hasPreviousStep: currentStepIndex > 0,
	};
};
